import { languageInfo } from '../i18n/languages'
import type { Language } from '../types/document'
import { isInLanguageScript } from './explain'

// Optional, user-initiated translation of the document's own lines, on the
// Samjho Worker. English lines go to the open-source AI4Bharat IndicTrans2
// model; lines in an Indian language (a Hindi letter for a Tamil reader, or
// into English) go to the AI model Ask uses. Everything else works without this.

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')
export const MAX_TRANSLATE_LINES = 12
const MAX_LINE_LENGTH = 400

export function translationConfigured(): boolean {
  return Boolean(API_BASE_URL) && import.meta.env.MODE !== 'test'
}

/** English lines can go to any Indian language (IndicTrans2). */
export function canTranslateTo(language: Language): boolean {
  return languageInfo(language).indicTrans !== null
}

// One language per script. Devanagari and Bengali script are shared (Hindi/
// Marathi, Bengali/Assamese); the first is used, and the model reads either.
const SCRIPT_LANGUAGES: Language[] = ['en', 'hi', 'bn', 'ta', 'te', 'gu', 'kn', 'ml', 'pa', 'or']

/** Which language a document line is written in, judged by its script. */
export function lineLanguage(text: string): Language | null {
  return SCRIPT_LANGUAGES.find((code) => isInLanguageScript(text, code)) ?? null
}

/** Lines worth translating for a reader of `language`, grouped by the language they are in. */
export function translatableLines(lines: string[], language: Language): Map<Language, string[]> {
  const groups = new Map<Language, string[]>()
  for (const line of lines) {
    if (isInLanguageScript(line, language)) continue
    const source = lineLanguage(line)
    if (!source || source === language) continue
    if (source === 'en' && !canTranslateTo(language)) continue
    groups.set(source, [...(groups.get(source) ?? []), line])
  }
  return groups
}

/** Removes personal identifiers before any text leaves the device. */
export function redactForNetwork(text: string): string {
  return text
    .replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, '[email]')
    .replace(/(?<!\d)\d{4}[\s-]?\d{4}[\s-]?\d{4}(?!\d)/g, '[number]')
    .replace(/\b[A-Z]{5}\d{4}[A-Z]\b/g, '[PAN]')
    .replace(/(?<!\d)(?:\+91[\s-]?)?[6-9]\d{4}[\s-]?\d{5}(?!\d)/g, '[phone]')
    .replace(/(?<!\d)\d{9,18}(?!\d)/g, '[number]')
}

export async function translateLines(lines: string[], language: Language, source: Language = 'en'): Promise<string[]> {
  if (!translationConfigured() || source === language || (source === 'en' && !canTranslateTo(language))) {
    throw new Error('translation-unavailable')
  }
  const texts = lines.slice(0, MAX_TRANSLATE_LINES).map((l) => redactForNetwork(l).slice(0, MAX_LINE_LENGTH))
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 25_000)
  try {
    const response = await fetch(`${API_BASE_URL}/api/translate`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(source === 'en' ? { texts, target: language } : { texts, target: language, source }),
      signal: controller.signal,
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    })
    if (!response.ok) throw new Error(`translation-http-${response.status}`)
    const body = (await response.json()) as { translations?: unknown }
    const out = body.translations
    if (!Array.isArray(out) || out.length !== texts.length || !out.every((t) => typeof t === 'string')) {
      throw new Error('translation-invalid')
    }
    return out as string[]
  } finally {
    clearTimeout(timer)
  }
}
